import {
  Box,
  Typography,
  Button,
  Fade,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Confetti from 'react-confetti';

const slideLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, ease: 'easeOut' },
  },
  exit: { opacity: 0, x: -40, transition: { duration: 0.25 } },
};

function FlagBar({ bg, fg, border, icon: IconCmp, text }) {
  if (!text) return null;
  return (
    <motion.div initial="hidden" animate="visible" exit="exit" variants={slideLeft}>
      <Box
        sx={{
          backgroundColor: bg,
          color: fg,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 1,
          px: 2,
          py: 1,
          mb: 1.25,
          borderLeft: `4px solid ${border}`,
          boxShadow: 1,
        }}
      >
        <IconCmp sx={{ fontSize: 18, mr: 1 }} />
        <Typography variant="body2">{text}</Typography>
      </Box>
    </motion.div>
  );
}

export default function InvoiceSuccessAlert({
  onNewInvoice,
  buttonText = 'Ingresar nueva factura',
  success,
  alert,
  error,
  subtitle,
}) {
  const [show, setShow] = useState({ success: false, alert: false, error: false });
  const [showConfetti, setShowConfetti] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Resize listener simple
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const hasSuccess = Boolean(success);
    const hasAlert = Boolean(alert);
    const hasError = Boolean(error);

    setShow({
      success: hasSuccess,
      alert: hasAlert,
      error: hasError,
    });

    // 🎉 Confetti solo éxito limpio
    if (hasSuccess && !hasAlert && !hasError) {
      setShowConfetti(true);

      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 3500);

      return () => clearTimeout(timer);
    } else {
      setShowConfetti(false);
    }
  }, [success, alert, error]);

  return (
    <Box sx={{ mt: 2, px: 2, maxWidth: 420, position: 'relative' }}>

      {/* 🎉 Confetti enterprise */}
      {showConfetti && (
        <Confetti
          width={dimensions.width}
          height={dimensions.height}
          numberOfPieces={120} // menos piezas = más elegante
          gravity={0.25}
          recycle={false}
          colors={[
            '#10173c', // azul corporativo oscuro
            '#1e2746',
            '#2e7d32', // verde éxito
            '#90caf9', // azul suave
          ]}
          initialVelocityY={20}
          tweenDuration={4000}
        />
      )}

      {/* Subtitle */}
      {subtitle && (
        <Fade in timeout={600}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 1.25, pl: 0.25, lineHeight: 1.4 }}
          >
            {subtitle}
          </Typography>
        </Fade>
      )}

      <AnimatePresence>
        {show.success && (
          <FlagBar
            bg="success.light"
            fg="success.contrastText"
            border="#2e7d32"
            icon={CheckCircleIcon}
            text={success}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {show.alert && (
          <FlagBar
            bg="warning.light"
            fg="warning.contrastText"
            border="#ed6c02"
            icon={WarningAmberIcon}
            text={alert}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {show.error && (
          <FlagBar
            bg="error.light"
            fg="error.contrastText"
            border="#d32f2f"
            icon={ErrorIcon}
            text={error}
          />
        )}
      </AnimatePresence>

      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onNewInvoice}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '0.875rem',
            px: 2.5,
            py: 1,
          }}
        >
          {buttonText}
        </Button>
      </motion.div>
    </Box>
  );
}
